import { useState } from 'react';
import { Reorder } from 'framer-motion';
import { initDataUser } from '@telegram-apps/sdk-react';

import { MaxRoster, MinRoster } from '@/api/generated/game';
import { useAppRuntime } from '@/app/runtime';
import { useBackButton } from '@/telegram';
import { MainAction } from '@/ui/MainAction';

import { DeckPicker } from './DeckPicker';
import { IntensityPicker } from './IntensityPicker';
import type { DraftPlayer } from './PlayerRow';
import { PlayerRow } from './PlayerRow';
import { describeError, playersWord } from './shared';

let seq = 0;
function nextKey(): string {
  seq += 1;
  return `draft-${seq}`;
}

function firstPlayer(): DraftPlayer {
  // Вне Telegram (обычный браузер, без мока) initDataUser() отдаёт undefined —
  // это не ошибка, хост просто впишет имя сам.
  const name = initDataUser()?.firstName ?? '';
  return { key: nextKey(), name, gender: 'unspecified' };
}

export interface HotseatSetupProps {
  onBack: () => void;
}

/**
 * Хост заводит весь состав сразу: имя и пол каждого, порядок рассадки —
 * порядком строк (design-spec.md, 02 · Настройка партии). Кода комнаты и
 * ожидания участников здесь нет.
 *
 * Тумблеров четырёх колод из макета здесь по-прежнему нет — в контракте нет
 * поля, которое бы их описывало. Раздел «ПАК» — про другое: это выбор версии
 * бандла (`deckVersion`), см. DeckPicker.
 */
export function HotseatSetup({ onBack }: HotseatSetupProps) {
  useBackButton(onBack);
  const runtime = useAppRuntime();
  const restClient = runtime?.restClient ?? null;

  const [players, setPlayers] = useState<DraftPlayer[]>(() => [firstPlayer()]);
  const [dragging, setDragging] = useState<string | null>(null);
  const [maxIntensity, setMaxIntensity] = useState(3);
  const [deckVersion, setDeckVersion] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const validCount = players.length >= MinRoster && players.length <= MaxRoster;
  const namesFilled = players.every((p) => p.name.trim().length > 0);
  const canStart = validCount && namesFilled && restClient != null && !pending;

  /** Почему кнопка не нажимается. Нативная MainButton гаснет молча. */
  const blocker = canStart
    ? null
    : pending
      ? 'Создаём комнату…'
      : players.length < MinRoster
        ? `Партия начинается от ${MinRoster} игроков.`
        : players.length > MaxRoster
          ? `За столом не больше ${MaxRoster} игроков.`
          : !namesFilled
            ? 'Впишите имя каждому игроку.'
            : null;

  function updatePlayer(next: DraftPlayer): void {
    setPlayers((prev) => prev.map((p) => (p.key === next.key ? next : p)));
  }

  function addPlayer(): void {
    if (players.length >= MaxRoster) return;
    setPlayers((prev) => [...prev, { key: nextKey(), name: '', gender: 'unspecified' }]);
  }

  async function handleStart(): Promise<void> {
    if (!restClient || !runtime || !canStart) return;
    setPending(true);
    setError(null);
    try {
      const room = await restClient.createRoom({
        roomMode: 'hotseat',
        gameMode: 'free',
        maxIntensity,
        players: players.map((p) => ({ name: p.name.trim(), gender: p.gender })),
        // Витрина не ответила — поля в запросе нет вовсе, версию выбирает
        // сервер. Писать `deckVersion: undefined` нельзя: при
        // exactOptionalPropertyTypes это не то же самое, что отсутствие поля.
        ...(deckVersion === null ? {} : { deckVersion }),
      });
      await runtime.connectToRoom(room.code);
    } catch (err) {
      setError(describeError(err));
      setPending(false);
    }
  }

  return (
    <div
      className="flex min-h-screen flex-col bg-ground px-5 text-ink"
      style={{ paddingTop: 'var(--clown-safe-top)' }}
    >
      <div className="flex-1 py-6">
        <h1 className="font-display text-task">КТО ИГРАЕТ</h1>

        <div className="mt-3 flex flex-col gap-rule border-2 border-line bg-line">
          <Reorder.Group as="div" axis="y" values={players} onReorder={setPlayers} className="contents">
            {players.map((player) => (
              <PlayerRow
                key={player.key}
                player={player}
                dragging={dragging === player.key}
                onChange={updatePlayer}
                onDragStart={() => setDragging(player.key)}
                onDragEnd={() => setDragging(null)}
              />
            ))}
          </Reorder.Group>

          <button
            type="button"
            onClick={addPlayer}
            disabled={players.length >= MaxRoster}
            className="flex h-row appearance-none items-center gap-3 rounded-none bg-panel px-4 text-left text-mute disabled:opacity-40"
          >
            <span className="text-lead">＋</span>
            <span className="text-lead">Добавить игрока</span>
          </button>
        </div>

        <IntensityPicker value={maxIntensity} onChange={setMaxIntensity} />
        <DeckPicker value={deckVersion} onChange={setDeckVersion} />

        {!restClient && (
          <p className="mt-4 text-base text-acc">Создание комнаты недоступно офлайн.</p>
        )}
      </div>

      {/*
        Отказ и причина выключенной кнопки живут рядом с самой кнопкой, а не в
        конце длинной формы: MainButton нативная и стоит на нижней кромке, а
        список игроков с пикерами длиннее экрана. Сообщение выше по странице
        человек не увидит вовсе — для него приложение просто не отреагирует.
      */}
      <div className="-mx-5" style={{ paddingBottom: 'var(--clown-safe-bottom)' }}>
        {error && <p className="mx-5 mb-2 text-base text-acc">{error}</p>}
        {!error && blocker && <p className="mx-5 mb-2 text-base text-mute">{blocker}</p>}
        <MainAction
          text={`НАЧАТЬ · ${players.length} ${playersWord(players.length)}`}
          enabled={canStart}
          onPress={handleStart}
        />
      </div>
    </div>
  );
}
